import { useQueryClient } from "@tanstack/react-query";
import { useMesfRealtime } from "@dexteel/mesf-core";

/**
 * Maps stored procedure names (broadcast via SignalR) to TanStack Query key
 * prefixes. When the backend executes a source SP that targets one of these
 * (configured in SYSTEM.QueryCacheInvalidations), the cache is invalidated
 * automatically for every connected client.
 *
 * Current triggers:
 *   [CAS].[UpsertMold]       -> RBD.GetCasterOfflinePartsTree  -> ["caster-mold-offline"]
 *   [CAS].[DeleteMold]       -> RBD.GetCasterOfflinePartsTree  -> ["caster-mold-offline"]
 *   [CAS].[UpsertStrandData] -> RBD.EQPGetCasterPartsTree      -> strands + offline
 */
const SP_TO_QUERY_KEYS: Record<string, readonly string[][]> = {
  "RBD.GetCasterOfflinePartsTree": [["caster-mold-offline"]],
  "RBD.EQPGetCasterPartsTree": [
    ["caster-mold-strand"],
    ["caster-mold-offline"],
  ],
};

/**
 * Listens to SignalR messages and invalidates Caster Mold query cache.
 *
 * - Offline tree: refreshes when molds are created/deleted from Configuration.
 * - Strand trees: refreshes when strand data is saved from CAS Data Entry.
 *
 * Call once at the CasterMoldPage level.
 */
export const useRealtimeInvalidation = () => {
  const queryClient = useQueryClient();

  useMesfRealtime({
    onReceiveMessage: (_author: string, message: string) => {
      const queryKeys = SP_TO_QUERY_KEYS[message];
      if (queryKeys) {
        queryKeys.forEach((queryKey) => {
          queryClient.invalidateQueries({ queryKey });
        });
      }
    },
  });
};
